<script setup lang="ts">
import type { HomepageBanner } from "~/stores/settings";

// Split out of `HomepageBanners.vue` so one piece of markup can render in all
// three places that need it: inside the client-only carousel, as that carousel's
// pre-hydration fallback, and as the single-banner case. The fallback is the
// reason it has to exist — without a server-rendered banner the strip is 0rem
// tall in the first paint and the whole page below it jumps down once the
// carousel mounts.
//
// Class names deliberately stay in the parent's `homepage-banners__*` namespace:
// they are the DOM contract the banner specs and the surrounding layout assert on.
defineProps<{ banner: HomepageBanner }>();
defineEmits<{ select: [HomepageBanner] }>();
</script>

<template>
  <button
    class="homepage-banners__banner"
    type="button"
    @click="$emit('select', banner)"
  >
    <span class="homepage-banners__frame">
      <picture>
        <source
          v-if="banner.mobileImageUrl"
          :srcset="banner.mobileImageUrl"
          media="(max-width: 768px)"
        />
        <img
          :src="banner.imageUrl"
          :alt="banner.name || 'Promotion'"
          decoding="async"
        />
      </picture>
    </span>
  </button>
</template>

<style lang="scss" scoped>
.homepage-banners__banner {
  display: block;
  width: 100%;
  /* Keep the button a plain full-width block. The aspect-ratio lives on the
     inner __frame span, NOT here: WebKit (all iOS browsers) mis-computes
     aspect-ratio on a <button> and on flex items — and in the carousel this
     button is a flex child whose width is set in px by Carousel/Base — which
     collapsed/stretched the banner height on iOS Firefox/Chrome. */
  min-width: 0;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  border-radius: 0.4rem;
  overflow: hidden;
}

.homepage-banners__frame {
  display: block;
  width: 100%;
  /* Wide desktop strip, 7.2:1 (design/upload target 1800×250). Image is
     covered + centred so any upload keeps this height. */
  aspect-ratio: 36 / 5;
  overflow: hidden;

  picture {
    display: block;
    width: 100%;
    height: 100%;
  }

  img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
  }

  @media (max-width: 768px) {
    /* Mobile banner, 2:1 (design/upload target 720×360). */
    aspect-ratio: 2 / 1;
  }
}
</style>
